import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useBranding } from "@/contexts/BrandingContext";
import { usePresentationMode } from "@/contexts/PresentationModeContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import PageNote from "@/components/PageNote";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TrendingUp, DollarSign, Calendar, Building2, CheckCircle2, XCircle, Clock, Globe, Settings, ExternalLink } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import PageDateFilter from "@/components/PageDateFilter";

// Nav menu dark color for chart text
const NAV_DARK_COLOR = "#0F172A";

// Format currency - shows K for thousands, M for millions (with 4 decimal places for M)
const formatCurrency = (value) => {
  if (value >= 1000000) {
    // Format as millions with up to 4 decimal places, max 5 total positions
    const millions = value / 1000000;
    return `$${millions.toFixed(4)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  } else {
    return `$${value.toFixed(0)}`;
  }
};

// Format for chart tooltips
const formatChartValue = (value) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(2)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  } else {
    return `$${value}`;
  }
};

// Generate alternating colors for pie charts to avoid adjacent same colors
const getAlternatingColors = (baseColors, count) => {
  if (count <= baseColors.length) {
    return baseColors.slice(0, count);
  }
  
  const result = [];
  for (let i = 0; i < count; i++) {
    // Alternate between colors by spreading them out
    const colorIndex = (i * 2) % baseColors.length;
    result.push(baseColors[colorIndex] || baseColors[i % baseColors.length]);
  }
  return result;
};

const DealsPage = () => {
  const { sessionToken, user } = useAuth();
  const navigate = useNavigate();
  const { chartColors, pageTitles } = useBranding();
  const { isPresentationMode, maskName, maskValue, maskAmount, maskNumber, maskSource } = usePresentationMode();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState(null);
  const [dateFilterInitialized, setDateFilterInitialized] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState("all");
  const [dealsData, setDealsData] = useState(null);
  const [configuredClosedWonStages, setConfiguredClosedWonStages] = useState([]);

  // CRITICAL: Track tenant ID in state to trigger re-fetches when tenant changes
  const [currentTenantId, setCurrentTenantId] = useState(() => sessionStorage.getItem("viewing_tenant_id"));
  
  // Listen for tenant changes
  useEffect(() => {
    const checkTenantChange = () => {
      const newTenantId = sessionStorage.getItem("viewing_tenant_id");
      if (newTenantId !== currentTenantId) {
        console.log("=== DealsPage: TENANT CHANGED ===", currentTenantId, "->", newTenantId);
        setCurrentTenantId(newTenantId);
      }
    };
    const interval = setInterval(checkTenantChange, 500);
    return () => clearInterval(interval);
  }, [currentTenantId]);

  const handleDateChange = useCallback((dateInfo) => {
    console.log("Deals date filter changed:", dateInfo);
    setDateFilter(dateInfo);
    setDateFilterInitialized(true);
  }, []);

  const fetchDealsData = async () => {
    setLoading(true);
    try {
      const viewingTenantId = currentTenantId;
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) params["X-Tenant-ID"] = viewingTenantId;
      if (dateFilter?.startDate) params.start_date = dateFilter.startDate;
      if (dateFilter?.endDate) params.end_date = dateFilter.endDate;
      if (selectedRegion && selectedRegion !== "all") params.region = selectedRegion;
      
      console.log("=== DealsPage fetchDealsData ===", "tenantId:", viewingTenantId);
      const response = await axios.get(`${API}/analytics/deals`, { params });
      console.log("Deals response:", response.data);
      setDealsData(response.data);
    } catch (error) {
      console.error("Error fetching deals:", error);
    } finally {
      setLoading(false);
    }
  };

  // Get deals from response or use empty defaults
  // Transform backend data to expected format
  const rawDeals = dealsData?.recent_deals || [];
  const deals = rawDeals.map(d => ({
    id: d.hubspot_id || d.id,
    name: d.name || d.dealname || 'Unnamed Deal',
    company: d.company || '-',
    owner: d.owner || '-',
    stage: d.stage_label || d.stage || d.dealstage || 'Unknown',
    value: parseFloat(d.amount) || 0,
    closeDate: d.close_date || d.closedate || '-',
    probability: d.probability || 0
  }));
  const stageDistribution = dealsData?.stage_distribution || [];
  const summary = dealsData?.summary || { total_deals: 0, pipeline_value: 0, active_pipeline: 0, won_value: 0, avg_value: 0 };

  // Use backend-calculated values for consistency
  const pipelineValue = summary.active_pipeline || summary.pipeline_value || 0;
  const wonValue = summary.won_value || 0;
  const activeDealsCount = summary.active_deals || deals.filter(d => d.stage && !d.stage.toLowerCase().includes("closed")).length;

  // Transform stage distribution for chart - use the label from backend
  const dealsByStage = stageDistribution.map(s => ({
    name: s.stage || s._id || 'Unknown',
    value: s.count || 0,
    amount: s.value || 0
  }));

  // Generate pipeline trend from recent deals (group by month)
  const pipelineTrend = [];
  const monthlyData = {};
  deals.forEach(deal => {
    const date = deal.closeDate;
    if (date && date !== '-') {
      try {
        const month = new Date(date).toLocaleString('en-US', { month: 'short' });
        if (!monthlyData[month]) {
          monthlyData[month] = { pipeline: 0, won: 0 };
        }
        const amount = deal.value || 0;
        monthlyData[month].pipeline += amount;
        if (deal.stage?.toLowerCase().includes('closed') && deal.stage?.toLowerCase().includes('won')) {
          monthlyData[month].won += amount;
        }
      } catch (e) {
        // Skip invalid dates
      }
    }
  });
  Object.entries(monthlyData).forEach(([month, data]) => {
    pipelineTrend.push({ month, ...data });
  });

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`, {
        params: { Authorization: `Bearer ${sessionToken}` },
      });
      setClients(response.data);
      if (response.data.length > 0) {
        setSelectedClient(response.data[0].client_id);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch configured Closed Won stages from tenant settings
  const fetchConfiguredStages = async () => {
    const viewingTenantId = currentTenantId || sessionStorage.getItem("viewing_tenant_id");
    if (!viewingTenantId) return;
    
    try {
      const params = { Authorization: `Bearer ${sessionToken}` };
      params["X-Tenant-ID"] = viewingTenantId;
      
      const response = await axios.get(`${API}/tenants/my-tenant-settings`, { params });
      const stages = response.data?.hubspot_stage_mapping?.closed_won_stages || [];
      console.log("Fetched Closed Won stages from tenant settings:", stages, "for tenant:", viewingTenantId);
      setConfiguredClosedWonStages(stages);
    } catch (error) {
      console.error("Error fetching configured stages:", error);
    }
  };

  // Check if current user is an admin
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || 
    user?.email?.endsWith('@jci-marketing.com') || 
    ['jason@jci-marketing.com', 'kevin@jci-marketing.com'].includes(user?.email);

  useEffect(() => {
    if (!dateFilterInitialized) return;
    fetchDealsData();
  }, [sessionToken, dateFilterInitialized, dateFilter?.startDate, dateFilter?.endDate, selectedRegion, currentTenantId]); // CRITICAL: Added currentTenantId

  useEffect(() => {
    fetchClients();
  }, [sessionToken, currentTenantId]); // CRITICAL: Added currentTenantId
  
  useEffect(() => {
    if (sessionToken && currentTenantId) {
      fetchConfiguredStages();
    }
  }, [sessionToken, currentTenantId]);
  
  // Listen for tenant-changed event
  useEffect(() => {
    const handleTenantChange = () => {
      const newTenantId = sessionStorage.getItem("viewing_tenant_id");
      if (newTenantId) {
        setCurrentTenantId(newTenantId);
      }
    };
    window.addEventListener("tenant-changed", handleTenantChange);
    return () => window.removeEventListener("tenant-changed", handleTenantChange);
  }, []);

  const getStageBadge = (stage) => {
    const variants = {
      "Demo Complete": { bg: "bg-blue-100", text: "text-blue-700", icon: Clock },
      "Proposal Sent": { bg: "bg-purple-100", text: "text-purple-700", icon: Clock },
      Negotiation: { bg: "bg-amber-100", text: "text-amber-700", icon: Clock },
      "Contract Review": { bg: "bg-orange-100", text: "text-orange-700", icon: Clock },
      "Closed Won": { bg: "bg-emerald-100", text: "text-emerald-700", icon: CheckCircle2 },
      "Closed Lost": { bg: "bg-red-100", text: "text-red-700", icon: XCircle },
    };
    const variant = variants[stage] || { bg: "bg-slate-100", text: "text-slate-700", icon: Clock };
    const Icon = variant.icon;
    return (
      <Badge className={`${variant.bg} ${variant.text} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {stage}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#00A5A8] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Use backend-calculated active deals
  const activeDeals = deals.filter(d => d.stage && !d.stage.toLowerCase().includes("closed"));

  return (
    <div className="animate-fade-in" data-testid="deals-page">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">{pageTitles?.deals || "Deals Pipeline"}</h1>
          <p className="text-sm text-slate-500 mt-1">
            Sales opportunities and pipeline management
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedRegion} onValueChange={setSelectedRegion}>
            <SelectTrigger className="w-36">
              <Globe className="w-4 h-4 mr-2 text-slate-500" />
              <SelectValue placeholder="Region" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Regions</SelectItem>
              <SelectItem value="na">NA</SelectItem>
              <SelectItem value="emea">EMEA</SelectItem>
              <SelectItem value="apac">APAC</SelectItem>
            </SelectContent>
          </Select>
          <PageDateFilter 
            pageId="deals" 
            defaultPreset="last_90_days"
            onDateChange={handleDateChange}
          />
          <Select value={selectedClient} onValueChange={setSelectedClient}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Select client" />
            </SelectTrigger>
            <SelectContent>
              {clients.map((client) => (
                <SelectItem key={client.client_id} value={client.client_id}>
                  {client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Page Notes & Market Insights */}
      <PageNote pageId="deals" />

      {/* Mapped Stages Indicator - Closed Won */}
      <Card 
        className={`border-emerald-200 bg-emerald-50/50 mb-6 ${isAdmin ? 'cursor-pointer hover:bg-emerald-100 transition-colors' : ''}`}
        onClick={isAdmin ? () => navigate('/app/admin?tab=features') : undefined}
      >
        <CardContent className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Settings className="w-4 h-4 text-emerald-600" />
              <div>
                <span className="text-sm font-medium text-emerald-800">Closed Won Stages: </span>
                {configuredClosedWonStages.length > 0 ? (
                  <span className="text-sm text-emerald-700">
                    {configuredClosedWonStages.join(', ')}
                  </span>
                ) : (
                  <span className="text-sm text-emerald-500 italic">
                    Using defaults (Closed Won, closedwon)
                  </span>
                )}
              </div>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-1 text-emerald-600 text-sm">
                <span>Edit in Settings</span>
                <ExternalLink className="w-3 h-3" />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-label">Active Pipeline</p>
                <p className="kpi-value">{isPresentationMode ? '$***M' : formatCurrency(pipelineValue)}</p>
                <p className="text-xs text-slate-500 mt-1">
                  {dateFilter?.startDate && dateFilter?.endDate 
                    ? `Close dates: ${dateFilter.startDate} to ${dateFilter.endDate}`
                    : 'All time'
                  }
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-[#00A5A8]/10 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-[#00A5A8]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-label">Closed Won</p>
                <p className="kpi-value text-emerald-600">{isPresentationMode ? '$***M' : formatCurrency(wonValue)}</p>
                <p className="text-xs text-slate-500 mt-1">
                  In selected period
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-label">Active Deals</p>
                <p className="kpi-value">{maskNumber(activeDealsCount)}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-label">Avg. Deal Size</p>
                <p className="kpi-value">{isPresentationMode ? '~$***K' : formatCurrency(activeDealsCount > 0 ? pipelineValue / activeDealsCount : 0)}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="chart-container">
          <CardHeader>
            <CardTitle style={{ color: NAV_DARK_COLOR }}>
              Pipeline & Won Revenue Trend {selectedRegion !== "all" && `(${selectedRegion.toUpperCase()})`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={pipelineTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" stroke={NAV_DARK_COLOR} fontSize={12} tick={{ fill: NAV_DARK_COLOR }} />
                <YAxis 
                  stroke={NAV_DARK_COLOR} 
                  fontSize={12} 
                  tick={{ fill: NAV_DARK_COLOR }} 
                  tickFormatter={isPresentationMode ? () => '~$***' : (v) => formatChartValue(v)} 
                />
                <Tooltip 
                  formatter={isPresentationMode ? () => ['~$***K', ''] : (value) => formatChartValue(value)} 
                  contentStyle={{ color: NAV_DARK_COLOR }} 
                />
                <Legend wrapperStyle={{ color: NAV_DARK_COLOR }} />
                <Bar dataKey="pipeline" fill={chartColors[0]} name="Pipeline" radius={[4, 4, 0, 0]} />
                <Bar dataKey="won" fill={chartColors[4] || "#10B981"} name="Won" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="chart-container">
          <CardHeader>
            <CardTitle style={{ color: NAV_DARK_COLOR }}>Deals by Stage</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Use horizontal bar chart for better readability with many stages */}
            <ResponsiveContainer width="100%" height={Math.max(300, dealsByStage.length * 35)}>
              <BarChart 
                data={dealsByStage.slice(0, 15)} 
                layout="vertical" 
                margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
                <XAxis 
                  type="number" 
                  stroke={NAV_DARK_COLOR} 
                  tick={{ fill: NAV_DARK_COLOR, fontSize: 12 }} 
                  tickFormatter={isPresentationMode ? () => '~**' : undefined}
                />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  stroke={NAV_DARK_COLOR} 
                  tick={{ fill: NAV_DARK_COLOR, fontSize: 11 }} 
                  width={95}
                  tickFormatter={(value) => value.length > 15 ? value.substring(0, 15) + '...' : value}
                />
                <Tooltip 
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                  formatter={isPresentationMode ? () => ['~**', 'Deals'] : (value, name, props) => [value, props.payload.name]}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {dealsByStage.slice(0, 15).map((entry, index) => {
                    const colors = getAlternatingColors(chartColors, dealsByStage.length);
                    return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {dealsByStage.length > 15 && (
              <p className="text-xs text-slate-500 mt-2 text-center">
                Showing top 15 stages. Total stages: {dealsByStage.length}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Deals Table */}
      <Card className="chart-container">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ color: NAV_DARK_COLOR }}>
            <TrendingUp className="w-5 h-5 text-[#00A5A8]" />
            {selectedRegion === "all" ? "All Regions" : selectedRegion.toUpperCase()} Deals
            {dateFilter?.startDate && dateFilter?.endDate && (
              <span className="text-sm font-normal text-slate-500 ml-2">
                ({dateFilter.startDate} to {dateFilter.endDate})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Deal Name</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead className="text-right">Value</TableHead>
                <TableHead>Close Date</TableHead>
                <TableHead className="text-right">Probability</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deals.map((deal, index) => (
                <TableRow key={deal.id} data-testid={`deal-row-${deal.id}`}>
                  <TableCell className="font-medium">
                    {isPresentationMode ? maskValue(deal.name, 'tenant') : deal.name}
                  </TableCell>
                  <TableCell>
                    {isPresentationMode ? maskValue(deal.company, 'tenant') : deal.company}
                  </TableCell>
                  <TableCell>
                    {isPresentationMode ? maskName(deal.owner, index) : deal.owner}
                  </TableCell>
                  <TableCell>{getStageBadge(deal.stage)}</TableCell>
                  <TableCell className="text-right font-semibold text-[#00A5A8]">
                    {isPresentationMode ? '$***,***' : `$${deal.value.toLocaleString()}`}
                  </TableCell>
                  <TableCell className="text-slate-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {deal.closeDate}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={deal.probability >= 70 ? "text-emerald-600" : deal.probability >= 40 ? "text-amber-600" : "text-red-500"}>
                      {deal.probability}%
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default DealsPage;
